import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Users, Star, CreditCard, LineChart, LogOut, Menu, X, KeyRound, ExternalLink, Wrench } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";

const NAV = [
  { to: "/admin", label: "Analytics", desc: "Traffic & growth", icon: LineChart, end: true },
  { to: "/admin/users", label: "Users", desc: "Accounts & roles", icon: Users },
  { to: "/admin/subscriptions", label: "Subscriptions", desc: "Billing & plans", icon: CreditCard },
  { to: "/admin/feedback", label: "Feedback", desc: "Ratings & notes", icon: Star },
  { to: "/admin/maintenance", label: "Maintenance", desc: "Data & resets", icon: Wrench },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const current = [...NAV].reverse().find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to))) || NAV[0];
  const initial = (user?.email?.[0] || "A").toUpperCase();

  return (
    <div className="min-h-screen w-full bg-slate-100 text-slate-900" data-ui="admin">
      {/* mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-[#0b0b1e] flex items-center justify-between px-4">
        <Logo size={24} showText textClass="text-sm" light />
        <button onClick={() => setOpen(true)} data-testid="admin-menu-btn" className="text-white p-2 -mr-2"><Menu size={22} /></button>
      </div>

      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="admin-backdrop" />}

      {/* sidebar */}
      <aside className={`fixed z-50 h-screen w-72 bg-[#0b0b1e] flex flex-col border-r border-white/5 transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <Logo size={30} showText textClass="text-base" light />
            <p className="text-[10px] font-bold tracking-[0.25em] text-cyan-300/70 mt-1.5 ml-9">ADMIN CONSOLE</p>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden text-slate-400 p-1"><X size={20} /></button>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto">
          <p className="px-3 pb-2 text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase">Management</p>
          {NAV.map(({ to, label, desc, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) => `group flex items-center gap-3 px-3 py-3 rounded-2xl transition-all duration-200 ${
                isActive ? "mel-gradient text-white shadow-[0_10px_28px_-10px_rgba(124,58,237,0.9)]" : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`}>
              {({ isActive }) => (
                <>
                  <span className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center transition-colors ${isActive ? "bg-white/20" : "bg-white/5 group-hover:bg-white/10"}`}>
                    <Icon size={18} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-tight">{label}</span>
                    <span className={`block text-[11px] leading-tight ${isActive ? "text-white/70" : "text-slate-500"}`}>{desc}</span>
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          <button onClick={() => { nav("/home"); }} data-testid="admin-back-to-app" className="w-full flex items-center gap-2 text-sm text-slate-300 hover:text-white font-medium px-1 transition-colors">
            <ExternalLink size={16} /> Back to app
          </button>
          <div className="flex items-center gap-3 bg-white/5 rounded-2xl p-3">
            <span className="w-9 h-9 shrink-0 rounded-full mel-gradient flex items-center justify-center text-white font-bold text-sm">{initial}</span>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-white truncate">{user?.email}</div>
              <div className="text-[10px] text-cyan-300/70 font-bold tracking-wide">ADMINISTRATOR</div>
            </div>
          </div>
          <div className="flex items-center justify-between px-1">
            <button onClick={() => { setPwOpen(true); setOpen(false); }} data-testid="admin-change-password-btn" className="flex items-center gap-1.5 text-xs text-cyan-300 font-medium"><KeyRound size={14} /> Credentials</button>
            <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="flex items-center gap-1.5 text-xs text-red-300 font-medium"><LogOut size={14} /> Log out</button>
          </div>
        </div>
      </aside>

      <AdminChangePassword open={pwOpen} onOpenChange={setPwOpen} />

      {/* main */}
      <div className="lg:ml-72 min-h-screen flex flex-col">
        <header className="hidden lg:flex items-center justify-between px-8 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-400 font-medium">Admin</span>
            <span className="text-slate-300">/</span>
            <span className="font-heading font-bold text-slate-800">{current.label}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{user?.email}</span>
            <span className="w-8 h-8 rounded-full mel-gradient flex items-center justify-center text-white font-bold text-xs">{initial}</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 pt-[72px] lg:pt-8 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
